"use client"

import classNames from "classnames"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { Button, Icon } from "../../components"
import DropdownUser from "../../components/Dropdown/DropdownUser"
import { SessionContext } from "../../routes/DashboardRoute"
import Scroll from "../Scroll"
import ProgressBar from "../../components/ProgressBar"
import { cancelRequest } from "../../utils/axios"
import { Api, getSession } from "../../api/Assinafy"

import logo from "../../assets/images/assinafy-logo.svg"
import "./styles.scss"
import { FixedAlert } from "../../modules/Billing/Alerts"
import { calendarDateFormat, formatBRL } from "../../utils/Formats"
import { formatContractUsage } from "./planUsage"
import { SUBSCRIPTION_UPDATED_EVENT } from "../../utils/subscriptionEvents"

const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(navigator.userAgent)

const Dashboard = ({ children, hideSidebar }) => {
  const [freePlan, setFreePlan] = useState(false)
  // Ʌ togggle pro botão de seja pro

  const [navbarShown, setNavbarShown] = useState(false)
  const [session] = useContext(SessionContext)
  const currentAccount = getSession().accounts[0]
  const [currentPlan, setCurrentPlan] = useState({})
  const [totalDocuments, setTotalDocuments] = useState(1)
  const location = useLocation()
  const isMountedRef = useRef(true)
  const isCustomContract = currentPlan?.type === "custom_contract"
  const pendingPayment = currentPlan?.pending_payment
  const hasDocumentLimitAlert = totalDocuments === 0 && !(currentPlan?.type === "custom_contract" && currentPlan?.balances?.documents?.limit === null)
  const isSandbox = process.env.REACT_APP_ENVIRONMENT === "sandbox"

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(navigator.userAgent)

  // const handleNavbarShown = () => {
  //   setNavbarShown(!navbarShown)
  // }

  const handleNavItemClick = () => {
    setNavbarShown(false)
  }

  const applyCurrentPlan = useCallback((data) => {
    if (!data) return

    setCurrentPlan(data)
    if (data?.type === "custom_contract") {
      if (data?.balances?.documents?.limit === null) {
        setTotalDocuments(1)
      } else {
        setTotalDocuments(data?.balances?.documents?.balance)
      }
    } else if (data?.max_documents) {
      setTotalDocuments(data?.balances?.documents?.balance)
    } else {
      setTotalDocuments(0)
    }
  }, [])

  const fetchCurrentPlan = useCallback((requestConfig = {}) => {
    if (!currentAccount?.id) return

    Api("subscription/view")(currentAccount.id, {
      ...requestConfig,
    }).then(({ status, data: { data } }) => {
      if (!isMountedRef.current) return
      if (status === 200) applyCurrentPlan(data)
    })
  }, [currentAccount?.id, applyCurrentPlan])

  useEffect(() => {
    if (isMobile()) {
      document.body.classList.add("is-mobile")
    } else {
      document.body.classList.remove("is-mobile")
    }
  }, [])

  useEffect(() => {
    const cancelToken = cancelRequest()
    fetchCurrentPlan(cancelToken.config)

    return () => {
      cancelToken.cancel()
    }
  }, [fetchCurrentPlan, location.key])

  useEffect(() => {
    const handleWindowFocus = () => fetchCurrentPlan()
    window.addEventListener("focus", handleWindowFocus)

    return () => {
      window.removeEventListener("focus", handleWindowFocus)
    }
  }, [fetchCurrentPlan])

  useEffect(() => {
    const handleSubscriptionUpdated = (event) => applyCurrentPlan(event.detail)
    window.addEventListener(SUBSCRIPTION_UPDATED_EVENT, handleSubscriptionUpdated)

    return () => {
      window.removeEventListener(SUBSCRIPTION_UPDATED_EVENT, handleSubscriptionUpdated)
    }
  }, [applyCurrentPlan])

  return (
    <>
      <div className="sandbox-alert">
        <FixedAlert
          show={isSandbox}
          text="Você está em uma ambiente de testes, nada aqui tem validade jurídica e não geram efeito reais."
          buttonText={false}
        />
      </div>

      {pendingPayment ? (
        <FixedAlert
          show={true}
          text={`Pagamento PIX pendente: ${formatBRL(pendingPayment.amount)} até ${calendarDateFormat(pendingPayment.due_date)}. Seu plano atual continua ativo até a confirmação.`}
          buttonText="Ver PIX"
          onClose={() => {
            window.location.href = "/dashboard/workspace/plan"
          }}
        />
      ) : hasDocumentLimitAlert && (
        <FixedAlert
          show={true}
          text={currentPlan?.type !== "custom_contract" ? "Limite de documentos atingido! Altere seu plano ou aguarde a renovação." : "Limite de documentos atingido! Compre documentos extras ou aguarde a renovação."}
          buttonText={currentPlan?.type !== "custom_contract" ? true : false}
          onClose={() => {
            window.location.href = "/dashboard/workspace/plan"
          }}
        />
      )}
      <div className={`dashboard ${hideSidebar ? "hideSidebar" : ""} ${isSandbox || pendingPayment || hasDocumentLimitAlert ? "plan-limit" : ""}`}>
        <header className="dashboard-header">
          <Link to={"/dashboard"} className="logo">
            <img src={logo || "/placeholder.svg"} height="24" alt="Assinafy logo" />
          </Link>
          <div className={`navbar${navbarShown ? " show" : ""}`}>
            <ul className="nav">
              <NavItems
                items={[
                  {
                    to: "/dashboard",
                    children: (
                      <>
                        <Icon id="icon-leaderboard" size="24" />
                        Início
                      </>
                    ),
                  },
                  {
                    to: "/dashboard/documents",
                    children: (
                      <>
                        <Icon id="icon-description" size="24" />
                        Documentos
                      </>
                    ),
                  },
                  {
                    to: "/dashboard/templates",
                    children: (
                      <>
                        <Icon id="icon-template" size="24" />
                        Templates
                      </>
                    ),
                  },
                  {
                    to: "/dashboard/signers",
                    children: (
                      <>
                        <Icon id="icon-group" size="24" />
                        Seus contatos
                      </>
                    ),
                  },
                  {
                    to: "/dashboard/integrations",
                    children: (<><Icon id="icon-integrations" size="24" />Integrações</>),
                  },
                  {
                    to: "/dashboard/workspace",
                    children: (
                      <>
                        <Icon id="icon-cog" size="24" />
                        Configurações
                      </>
                    ),
                  },
                ]}
                onClick={handleNavItemClick}
              />
            </ul>
            {/* V provavelmente poderia ser um elemento próprio */}
            <div className="plan-use d-none d-lg-block">
            <div className="d-flex align-items-center justify-content-between">
                <p className="plan-use-title">Plano {currentPlan?.plan?.name} {isCustomContract && "Personalizado"}</p>
                {!isCustomContract && (
                  <Link
                    to="/dashboard/workspace/plan"
                    title={pendingPayment ? "Pagamento PIX pendente" : undefined}
                  >
                    {pendingPayment ? "PIX Pendente" : "Alterar plano"}
                  </Link>
                )}
              </div>
              {isCustomContract ? (
                <>
                  <p className="plan-use-period">Documentos usados</p>
                  <div className="plan-use-bottom d-flex justify-content-between mt-2">
                    <p>Documentos</p>
                    <span>{formatContractUsage(currentPlan?.balances?.documents)}</span>
                  </div>
                  <div className="plan-use-bottom d-flex align-items-center justify-content-between mt-2">
                    <p>Créditos</p>
                    <span>{formatContractUsage(currentPlan?.balances?.credits, formatBRL)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="plan-use-bottom d-flex justify-content-between mt-2">
                    <p>Documentos</p>
                    <span>{currentPlan?.balances?.documents?.limit === null ? "Sem limite" : `${currentPlan?.balances?.documents?.balance !== undefined ? currentPlan?.balances?.documents?.balance : ''}`}</span>
                  </div>
                  <div className="plan-use-bottom d-flex align-items-center justify-content-between mt-2">
                    <p>Créditos</p>
                    <span>{currentPlan?.balances?.credits?.balance !== undefined ? formatBRL(currentPlan?.balances?.credits?.balance) : ''}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
        <div className="dropdown-wrapper">
          {freePlan && <Button variant="upgrade">Seja PRO</Button>}
          <DropdownUser session={session} onClick={handleNavItemClick} />
        </div>
        <Scroll>
          {children}
        </Scroll>
      </div>
    </>
  )
}

const DashboardContent = (props) => {
  return <div className="dashboard-content" {...props} />
}

const DashboardTitle = ({ as: Component = "h1", ...props }) => {
  return <Component {...props} className="dashboard-title" />
}

const DashboardOpening = (props) => {
  return <div {...props} className="dashboard-opening" />
}

const DashboardHeader = ({ returnButton, returnHref, title, titleAs, opening, ...props }) => {
  const { children, className } = props
  const newClassName = classNames("dashboard-subheader", className)
  const resolvedReturnHref = returnHref || "/dashboard/workspace/plan"

  return (
    <div {...props} className={newClassName}>
      <div className={`${returnButton ? "dashboard-subheader-with-return" : ""}`}>
        {returnButton && (
          <Button as="a" variant="border" href={resolvedReturnHref}>
            <Icon size={16} id="icon-arrow_left_2" />
          </Button>
        )}
        <DashboardTitle as={titleAs}>{title}</DashboardTitle>
        {opening && <DashboardOpening>{opening}</DashboardOpening>}
      </div>
      {children}
    </div>
  )
}

const DashboardFilter = (props) => {
  return <div {...props} className="dashboard-filter" />
}
const DashboardFixedBottom = (props) => {
  useEffect(() => {
    const filterFixed = document.querySelector(".dashboard-fixed-bottom")
    window.addEventListener("scroll", () => {
      if (window.scrollY > 20) {
        filterFixed.classList.add("scroll-top")
      } else {
        filterFixed.classList.remove("scroll-top")
      }
    })
  }, [])

  return <div {...props} className="dashboard-fixed-bottom" />
}

function NavItems({ items, onClick }) {
  let active

  items.map(({ to }, i) => {
    if (window.location.pathname.match(to)) {
      active = i
    }
  })

  return (
    <>
      {items.map((props, i) => {
        const newClassName = classNames("nav-link", active === i ? "active" : "")

        return (
          <li key={i} className="nav-item">
            <Link {...props} className={newClassName} onClick={onClick} />
          </li>
        )
      })}
    </>
  )
}

Dashboard.Content = DashboardContent
Dashboard.Filter = DashboardFilter
Dashboard.FixedBottom = DashboardFixedBottom
Dashboard.Header = DashboardHeader
Dashboard.Title = DashboardTitle
Dashboard.Opening = DashboardOpening

export default Dashboard
